import { hasComponent } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { spawnFx } from '@src/ecs/archetypes';
import { Enemy, EnemyKind, Health, Player, Position, Spawner, Sprite, SpriteId, Velocity } from '@src/ecs/components';
import { emitParticles, ParticleKind, type GameContext } from '@src/ecs/world';
import { FirePattern, weaponDef } from '@src/game/weapons/WeaponDefs';

/**
 * Applies damage and knockback to a target. Death itself is resolved later in
 * DamageSystem so every system sees a consistent world during the tick.
 */
export function dealDamage(ctx: GameContext, target: number, amount: number, kbX: number, kbY: number, spawnerMult: number, weaponId: number): boolean {
  if (Health.hp[target] <= 0) return false;
  if (Health.invuln[target] > 0) return false;

  const world = ctx.world;
  const x = Position.x[target];
  const y = Position.y[target];
  if (hasComponent(world, target, Spawner)) {
    const dmg = amount * spawnerMult;
    if (dmg <= 0) return false;
    Health.hp[target] -= dmg;
    const maxHp = Health.max[target] || 1;
    const wound = 1 - Math.max(0, Math.min(1, Health.hp[target] / maxHp));
    Sprite.squash[target] = Math.max(Sprite.squash[target], 0.42 + wound * 0.22);
    Sprite.flash[target] = Math.max(Sprite.flash[target], Math.min(1, 0.55 + dmg * 0.06 + wound * 0.2));
    Sprite.joltX[target] = clampAbs(Sprite.joltX[target] + kbX * Config.anim.joltPerKnockback * 0.65, 0.34);
    Sprite.joltY[target] = clampAbs(Sprite.joltY[target] + kbY * Config.anim.joltPerKnockback * 0.65, 0.34);
    const beam = weaponDef(weaponId).pattern === FirePattern.Beam;
    if (!beam || ctx.rng.chance(0.06)) ctx.events.push(GameEvent.SpawnerHit, target, weaponId, x, y);
    hitFx(ctx, weaponId, x - kbX * 0.08, y - kbY * 0.08, kbX, kbY, 0.9 + wound * 0.25);
    if (wound > 0.28 && (!beam || ctx.rng.chance(0.1))) {
      emitParticles(ctx.fx, ParticleKind.PortalBurst, x, y, kbX, kbY, 0.22 + wound * 0.4);
    }
    if (dmg >= 6) {
      ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs);
      ctx.fx.shakeX += kbX * Config.fx.spawnerShake;
      ctx.fx.shakeY += kbY * Config.fx.spawnerShake;
      ctx.fx.rumble = Math.max(ctx.fx.rumble, 1);
    }
    return true;
  }

  if (hasComponent(world, target, Enemy)) {
    if (Enemy.kind[target] === EnemyKind.Boss) {
      // The archmage does not get shoved. Damage reads as a flash, a jolt and sparks;
      // BossSystem owns the flinch clip and AudioDirector throttles the hurt cue.
      Health.hp[target] -= amount;
      Sprite.flash[target] = Math.max(Sprite.flash[target], Math.min(1, 0.55 + amount * 0.04));
      Sprite.squash[target] = Math.max(Sprite.squash[target], 0.2);
      const j = Config.anim.joltPerKnockback * 0.5;
      Sprite.joltX[target] = clampAbs(Sprite.joltX[target] + kbX * j, 0.3);
      Sprite.joltY[target] = clampAbs(Sprite.joltY[target] + kbY * j, 0.3);
      Enemy.stagger[target] = Math.max(Enemy.stagger[target], amount >= 8 ? 0.3 : 0.15);
      ctx.events.push(GameEvent.BossHit, target, Math.round(amount), x, y);
      if (Health.hp[target] > 0) {
        hitFx(ctx, weaponId, x, y - 0.6, kbX, kbY, 0.7);
        if (amount >= 8) {
          ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs * 0.4);
          ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.3);
        }
      }
      return true;
    }
    Health.hp[target] -= amount;
    const kind = Enemy.kind[target];
    const golem = kind === EnemyKind.Golem || kind === EnemyKind.Brute;
    const wallGun = kind === EnemyKind.Gargoyle || kind === EnemyKind.Spitter;
    // A dormant chest takes the hit like stone: it wakes (EnemyAISystem sees hp < max) but does not skid.
    const planted = golem || wallGun || (kind === EnemyKind.Mimic && Enemy.mode[target] < 2);
    const resistRule = kind === EnemyKind.Brute ? Config.enemies.brute.knockbackResist : Config.enemies.golem.knockbackResist;
    const resist = planted ? (golem ? 1 - resistRule : 0) : 1;
    Velocity.x[target] += kbX * resist;
    Velocity.y[target] += kbY * resist;
    Sprite.flash[target] = 1;
    if (planted) {
      // The golem barely moves from knockback, so the reaction has to be visual:
      // a flinch clip, a body jolt along the hit, a white flash and stone chips.
      Enemy.stagger[target] = Math.max(Enemy.stagger[target], Config.anim.golemStagger);
      Sprite.squash[target] = Math.max(Sprite.squash[target], 0.45);
      const j = Config.anim.joltPerKnockback;
      Sprite.joltX[target] = clampAbs(Sprite.joltX[target] + kbX * j, 0.45);
      Sprite.joltY[target] = clampAbs(Sprite.joltY[target] + kbY * j, 0.45);
      if (Health.hp[target] > 0) {
        hitFx(ctx, weaponId, x, y, kbX, kbY, 0.8);
        // Beams land every tick; chip the golem a few times a second instead of every hit.
        const beam = weaponDef(weaponId).pattern === FirePattern.Beam;
        if (!beam || ctx.rng.chance(0.05)) emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y + 0.3, kbX, kbY, 0.35);
        ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.35);
        if (amount >= 8) {
          ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, Config.fx.hitStopMs * 0.5);
          ctx.fx.shakeX += kbX * 0.02;
          ctx.fx.shakeY += kbY * 0.02;
        }
      }
    } else {
      Enemy.stagger[target] = Math.max(Enemy.stagger[target], 0.12);
      Sprite.squash[target] = Math.max(Sprite.squash[target], 0.6);
      if (Health.hp[target] > 0) hitFx(ctx, weaponId, x, y, kbX, kbY, 0.4);
    }
    if (Health.hp[target] > 0) {
      // Beams tick every frame; keep the event queue (and the hit cue) to a few per second.
      const beam = weaponDef(weaponId).pattern === FirePattern.Beam;
      if (!beam || ctx.rng.chance(0.06)) ctx.events.push(GameEvent.EnemyHit, weaponId, Enemy.kind[target], x, y);
    }
    return true;
  }

  if (hasComponent(world, target, Player)) {
    // One choke point for every hit on the wizard: contact, wall bolts, boss spells, dragon fire.
    amount *= difficulty().damageTaken;
    Health.hp[target] -= amount;
    Health.invuln[target] = Config.player.invulnAfterHit;
    Velocity.x[target] += kbX;
    Velocity.y[target] += kbY;
    Sprite.squash[target] = Math.max(Sprite.squash[target], 0.5);
    ctx.events.push(GameEvent.PlayerHit, target, Math.round(amount), x, y);
    emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, kbX, kbY, 0.8);
    ctx.fx.shakeX += kbX * 0.08;
    ctx.fx.shakeY += kbY * 0.08;
    ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.6);
    return true;
  }

  Health.hp[target] -= amount;
  return true;
}

function clampAbs(v: number, limit: number): number {
  return v > limit ? limit : v < -limit ? -limit : v;
}

/** Weapon-flavored impact: lightning gets its own burst sprite, everything else sparks. */
function hitFx(ctx: GameContext, weaponId: number, x: number, y: number, dirX: number, dirY: number, power: number): void {
  const pattern = weaponDef(weaponId).pattern;
  if (pattern === FirePattern.Beam) {
    // Beam damage lands every tick; keep the burst sprite to a few per second.
    if (ctx.rng.chance(0.04)) spawnFx(ctx, SpriteId.LightningHit, x, y, ctx.rng.next() * Math.PI * 2, 0.9 + power * 0.5, 0xbfffff);
    return;
  }
  emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, dirX, dirY, power);
  if (power >= 0.7 && ctx.rng.chance(0.5)) spawnFx(ctx, SpriteId.Spark, x, y, ctx.rng.next() * Math.PI * 2, 0.5 + power * 0.4);
}
